import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Truck, LogOut, Plus, RefreshCcw, Search, X, Wifi, WifiOff, Inbox, CalendarDays, AlertTriangle, BarChart3, Shield, Bell, BellOff, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useBrowserNotifications, shouldNotifyRole, STAGE_LABEL } from "@/hooks/useNotifications";
import EkinBrand from "@/components/EkinBrand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DeliveryForm from "@/components/DeliveryForm";
import KanbanColumn from "@/components/KanbanColumn";
import HistoryModal from "@/components/HistoryModal";
import ReceiverModal from "@/components/ReceiverModal";
import DriverPickerModal from "@/components/DriverPickerModal";

const STAGES = [
  { id: "factura", name: "Factura" },
  { id: "embarque", name: "Embarque" },
  { id: "en_ruta", name: "En Ruta" },
  { id: "entregado", name: "Entregado" },
];

const ROLE_LABEL = {
  ventas: "Ventas",
  contador: "Contador",
  logistica: "Logística",
  chofer: "Chofer",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState("factura");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [myQueueOnly, setMyQueueOnly] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [receiverFor, setReceiverFor] = useState(null);
  const [driverPickerFor, setDriverPickerFor] = useState(null);   // { delivery, mode }
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);
  const { supported: notifSupported, permission: notifPermission, requestPermission, notify } = useBrowserNotifications();

  const fetchDeliveries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deliveries`);
      setDeliveries(res.data);
    } catch {
      toast.error("Error cargando entregas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // SSE real-time updates
  useEffect(() => {
    if (!user) return;
    const url = `${API}/events`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.addEventListener("hello", () => setConnected(true));

    const upsert = (delivery) => {
      setDeliveries((prev) => {
        const idx = prev.findIndex((d) => d.id === delivery.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = delivery;
          return next;
        }
        return [delivery, ...prev];
      });
    };

    es.addEventListener("delivery_created", (ev) => {
      const data = JSON.parse(ev.data);
      upsert(data.delivery);
      if (data.delivery.created_by !== user.user_id) {
        toast.info(`Nueva entrega: ${data.delivery.cliente}`);
      }
      // Windows / browser notification for contador (new delivery enters factura stage)
      if (data.delivery.created_by !== user.user_id && shouldNotifyRole(user.role, data.delivery.stage)) {
        notify(`Nueva entrega para ${STAGE_LABEL[data.delivery.stage] || data.delivery.stage}`, {
          body: `${data.delivery.cliente} · ${data.delivery.herramienta}`,
          tag: `delivery-${data.delivery.id}`,
        });
      }
    });
    es.addEventListener("delivery_updated", (ev) => {
      const data = JSON.parse(ev.data);
      upsert(data.delivery);
      // Notify the role responsible for the new stage (excluding the user who triggered it)
      if (shouldNotifyRole(user.role, data.delivery.stage)) {
        const lastHist = data.delivery.history?.[data.delivery.history.length - 1];
        if (lastHist && lastHist.by !== user.name) {
          notify(`Entrega ahora en ${STAGE_LABEL[data.delivery.stage] || data.delivery.stage}`, {
            body: `${data.delivery.cliente} · ${data.delivery.herramienta}`,
            tag: `delivery-${data.delivery.id}`,
          });
        }
      }
    });
    es.addEventListener("delivery_deleted", (ev) => {
      const data = JSON.parse(ev.data);
      setDeliveries((prev) => prev.filter((d) => d.id !== data.delivery_id));
    });

    es.onerror = () => {
      setConnected(false);
    };

    return () => {
      es.close();
      setConnected(false);
    };
  }, [user, notify]);

  const handleAdvanceClick = (delivery) => {
    // If next stage is "entregado" (i.e., current is en_ruta), prompt for receiver name
    if (delivery.stage === "en_ruta") {
      setReceiverFor(delivery);
      return;
    }
    // If current stage is "embarque" and the user is logistica/master, must pick a driver first
    if (delivery.stage === "embarque" && (user?.role === "logistica" || user?.role === "master" || user?.role === "administrador")) {
      setDriverPickerFor({ delivery, mode: "assign" });
      return;
    }
    doAdvance(delivery.id);
  };

  const handleReassignClick = (delivery) => {
    setDriverPickerFor({ delivery, mode: "reassign" });
  };

  const doAdvance = async (id, recibidoPor, evidenciaPath, gps, assignedDriverId) => {
    try {
      const body = {};
      if (recibidoPor) body.recibido_por = recibidoPor;
      if (evidenciaPath) body.evidencia_path = evidenciaPath;
      if (assignedDriverId) body.assigned_driver_id = assignedDriverId;
      if (gps) {
        body.latitude = gps.lat;
        body.longitude = gps.lng;
      }
      await axios.post(`${API}/deliveries/${id}/advance`, body);
      toast.success(recibidoPor ? "Entrega confirmada" : "Etapa avanzada");
      setReceiverFor(null);
      setDriverPickerFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo avanzar");
    }
  };

  const doReassign = async (delivery, driver) => {
    try {
      await axios.post(`${API}/deliveries/${delivery.id}/reassign`, { driver_id: driver.user_id });
      toast.success(`Reasignada a ${driver.name}`);
      setDriverPickerFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reasignar");
    }
  };

  const handleRouteStarted = (updated) => {
    setDeliveries((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  };

  const handleDeleted = (deletedId) => {
    setDeliveries((prev) => prev.filter((d) => d.id !== deletedId));
  };

  const handleCreate = async (payload) => {
    await axios.post(`${API}/deliveries`, payload);
    toast.success("Entrega creada");
    setShowForm(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deliveries.filter((d) => {
      if (q) {
        const matches =
          d.cliente.toLowerCase().includes(q) ||
          d.herramienta.toLowerCase().includes(q) ||
          (d.recibido_por || "").toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (dateFrom && d.fecha_entrega < dateFrom) return false;
      if (dateTo && d.fecha_entrega > dateTo) return false;
      if (myQueueOnly) {
        const owned = {
          contador: ["factura"],
          logistica: ["embarque"],
          chofer: ["en_ruta"],
          ventas: [],
        }[user?.role] || [];
        if (!owned.includes(d.stage)) return false;
      }
      return true;
    });
  }, [deliveries, search, dateFrom, dateTo, myQueueOnly, user]);

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.id] = filtered.filter((d) => d.stage === s.id);
    return acc;
  }, {});

  const todayIso = new Date().toISOString().slice(0, 10);
  const isOverdue = (d) => d.stage !== "entregado" && d.fecha_entrega < todayIso;
  const overdueCount = filtered.filter(isOverdue).length;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top Nav */}
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 backdrop-saturate-150 border-b border-zinc-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EkinBrand subtitle="Tablero de entregas" />
            <span
              className={`hidden sm:inline-flex items-center gap-1 text-[10px] eyebrow ml-1 ${connected ? "text-emerald-700" : "text-zinc-400"}`}
              data-testid="sse-status"
            >
              {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {connected ? "En vivo" : "Sin conexión"}
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="eyebrow text-zinc-500" data-testid="user-role-label">
                {ROLE_LABEL[user?.role] || "Sin rol"}
              </span>
            </div>
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 object-cover" />}
            <button
              onClick={fetchDeliveries}
              data-testid="btn-refresh"
              className="btn-tactile p-2 hover:bg-zinc-100 transition-colors"
              title="Refrescar"
            >
              <RefreshCcw className="h-4 w-4" />
            </button>
            <Link
              to="/metrics"
              data-testid="link-metrics"
              className={`btn-tactile hidden sm:inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors ${["master", "administrador"].includes(user?.role) ? "" : "hidden sm:!hidden"}`}
              title="Métricas"
              style={{ display: ["master", "administrador"].includes(user?.role) ? undefined : "none" }}
            >
              <BarChart3 className="h-4 w-4" />
              <span>Métricas</span>
            </Link>
            {["master", "administrador"].includes(user?.role) && (
              <Link
                to="/reports"
                data-testid="link-reports"
                className="btn-tactile hidden sm:inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors"
                title="Reportes"
              >
                <FileText className="h-4 w-4" />
                <span>Reportes</span>
              </Link>
            )}
            {["master", "administrador"].includes(user?.role) && (
              <Link
                to="/admin"
                data-testid="link-admin"
                className="btn-tactile hidden sm:inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors"
                title="Administración"
              >
                <Shield className="h-4 w-4" />
                <span>Admin</span>
              </Link>
            )}
            <button
              onClick={logout}
              data-testid="btn-logout"
              className="btn-tactile flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        {/* Browser notification permission banner */}
        {notifSupported && notifPermission === "default" && ["contador", "logistica", "chofer", "entregas_locales"].includes(user?.role) && (
          <div className="mb-6 border-2 bg-white px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "#1A3A8C" }} data-testid="notif-permission-banner">
            <div className="flex items-center gap-3 min-w-0">
              <Bell className="h-5 w-5 flex-shrink-0" style={{ color: "#1A3A8C" }} />
              <div className="min-w-0">
                <p className="text-sm font-medium" style={{ color: "#0B1B3E" }}>
                  Activa las notificaciones de Windows
                </p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  Recibe un aviso emergente cuando entren entregas a tu etapa, aunque la pestaña esté en segundo plano.
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                const r = await requestPermission();
                if (r === "granted") toast.success("Notificaciones activadas");
                else if (r === "denied") toast.error("Permiso denegado. Actívalo desde el candado del navegador.");
              }}
              data-testid="btn-enable-notifications"
              className="btn-tactile flex-shrink-0 px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: "#1A3A8C" }}
            >
              Activar
            </button>
          </div>
        )}
        {notifSupported && notifPermission === "denied" && ["contador", "logistica", "chofer", "entregas_locales"].includes(user?.role) && (
          <div className="mb-6 border border-zinc-200 bg-zinc-50 px-4 py-2 flex items-center gap-3 text-xs text-zinc-600" data-testid="notif-denied-banner">
            <BellOff className="h-4 w-4 text-zinc-400" />
            <span>Notificaciones de escritorio bloqueadas. Para activarlas: ícono de candado del navegador → Notificaciones → Permitir.</span>
          </div>
        )}

        {/* Header / Action */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <p className="eyebrow text-zinc-500 mb-2">Tablero de entregas</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              {filtered.length} entrega{filtered.length === 1 ? "" : "s"}
              {search && <span className="text-zinc-400"> · filtrado</span>}
            </h1>
            {overdueCount > 0 && (
              <div
                className="mt-3 inline-flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1.5 text-xs font-medium"
                data-testid="overdue-counter"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {overdueCount} entrega{overdueCount === 1 ? "" : "s"} atrasada{overdueCount === 1 ? "" : "s"}
              </div>
            )}
          </div>

          {user?.role === "ventas" && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="btn-new-delivery"
              className="btn-tactile inline-flex items-center gap-2 bg-ekin-600 text-white px-5 py-3 text-sm font-medium hover:bg-ekin-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva entrega
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, herramienta o receptor..."
              data-testid="input-search"
              className="w-full pl-10 pr-10 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-zinc-950 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="btn-clear-search"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100"
              >
                <X className="h-4 w-4 text-zinc-500" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2">
              <CalendarDays className="h-4 w-4 text-zinc-500" />
              <span className="eyebrow text-zinc-500 text-[10px]">Fechas</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="input-date-from"
                className="text-xs border-none outline-none bg-transparent"
              />
              <span className="text-zinc-400">—</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="input-date-to"
                className="text-xs border-none outline-none bg-transparent"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  data-testid="btn-clear-dates"
                  className="ml-1 text-zinc-500 hover:text-zinc-900"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {user?.role && !["ventas", "master", "consulta", "administrador"].includes(user.role) && (
              <button
                onClick={() => setMyQueueOnly(!myQueueOnly)}
                data-testid="btn-my-queue-toggle"
                className={`btn-tactile inline-flex items-center gap-2 px-4 py-2 text-xs border transition-colors ${myQueueOnly ? "bg-ekin-600 text-white border-ekin-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
              >
                <Inbox className="h-3.5 w-3.5" />
                <span className="font-medium">Mi cola</span>
                {myQueueOnly && <span className="bg-white/20 px-1.5 py-0.5 text-[10px]">activo</span>}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* Desktop: 4 columns */}
            <div className="hidden lg:grid grid-cols-4 gap-4" data-testid="kanban-desktop">
              {STAGES.map((s) => (
                <KanbanColumn
                  key={s.id}
                  stage={s.id}
                  name={s.name}
                  items={grouped[s.id]}
                  userRole={user?.role}
                  currentUserId={user?.user_id}
                  onAdvance={handleAdvanceClick}
                  onOpenHistory={setHistoryItem}
                  onRouteStarted={handleRouteStarted}
                  onDelete={handleDeleted}
                  onReassign={handleReassignClick}
                />
              ))}
            </div>

            {/* Mobile: Tabs */}
            <div className="lg:hidden" data-testid="kanban-mobile">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-4 w-full bg-white border border-zinc-200 h-auto p-0">
                  {STAGES.map((s) => (
                    <TabsTrigger
                      key={s.id}
                      value={s.id}
                      data-testid={`tab-${s.id}`}
                      className="data-[state=active]:bg-ekin-600 data-[state=active]:text-white py-3 text-xs font-medium border-r border-zinc-200 last:border-r-0"
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <span>{s.name}</span>
                        <span className="text-[10px] opacity-70">{grouped[s.id].length}</span>
                      </div>
                    </TabsTrigger>
                  ))}
                </TabsList>
                {STAGES.map((s) => (
                  <TabsContent key={s.id} value={s.id} className="mt-4">
                    <KanbanColumn
                      stage={s.id}
                      name={s.name}
                      items={grouped[s.id]}
                      userRole={user?.role}
                      currentUserId={user?.user_id}
                      onAdvance={handleAdvanceClick}
                      onOpenHistory={setHistoryItem}
                      onRouteStarted={handleRouteStarted}
                      onDelete={handleDeleted}
                      onReassign={handleReassignClick}
                      mobileMode
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </>
        )}
      </main>

      {showForm && <DeliveryForm onClose={() => setShowForm(false)} onSubmit={handleCreate} />}
      {historyItem && <HistoryModal delivery={historyItem} onClose={() => setHistoryItem(null)} />}
      <ReceiverModal
        open={!!receiverFor}
        deliveryCliente={receiverFor?.cliente}
        onClose={() => setReceiverFor(null)}
        onConfirm={(name, evidenciaPath, gps) => doAdvance(receiverFor.id, name, evidenciaPath, gps)}
      />
      {driverPickerFor && (
        <DriverPickerModal
          delivery={driverPickerFor.delivery}
          mode={driverPickerFor.mode}
          onClose={() => setDriverPickerFor(null)}
          onConfirm={(driver) => {
            if (driverPickerFor.mode === "reassign") {
              return doReassign(driverPickerFor.delivery, driver);
            }
            return doAdvance(driverPickerFor.delivery.id, null, null, null, driver.user_id);
          }}
        />
      )}
    </div>
  );
}
